import React, { useEffect, useMemo, useState } from "react";
import { createProductPriceQuote } from "../presentation/money";
import {
  resolveInitialVariantState,
  resolveProductAvailability,
} from "../presentation/availability";
import AvailabilityToken from "./AvailabilityToken";
import PriceBlock from "./PriceBlock";
import PrimaryVariantChips from "./PrimaryVariantChips";
import PromotionBadge from "./PromotionBadge";
import productPlaceholder from "../assets/product-placeholder.png";
import { productDisplayName } from "../presentation/productCopy";
import { productImageFit } from "../presentation/imageFit";
import "./SharedProductCard.css";
import DietaryIcons from "./DietaryIcons";

const getContainerPresentation = (surface, preset) => {
  if (surface === "scroll") {
    return { className: "px-2", style: { flex: "0 0 170px", width: 170 } };
  }
  if (surface === "grid" || surface === "bubbles") {
    const singleColumn = preset.card.density === "single-column";
    return {
      className: `${singleColumn ? "col-12" : "col-6"} mb-4 px-2`,
      style: { width: singleColumn ? "100%" : "50%" },
    };
  }
  return { className: "mb-3 px-2", style: { width: "100%" } };
};

const eventStartedInNestedControl = (event) => {
  if (event.target === event.currentTarget || !event.target.closest) return false;
  const control = event.target.closest(
    "a,button,input,select,textarea,[role='button'],[role='link']",
  );
  return Boolean(control && control !== event.currentTarget);
};

const resolveVisualSlot = (slot, product, context) =>
  typeof slot === "function" ? slot(product, context) : slot;

const getVariantSelection = (variantProduct = {}) =>
  (Array.isArray(variantProduct.variant_values)
    ? variantProduct.variant_values
    : []
  ).reduce((nextSelection, value) => {
    if (value.variant_key && value.variant_value) {
      nextSelection[value.variant_key] = value.variant_value;
    }
    return nextSelection;
  }, {});

const SharedProductCard = ({
  product,
  surface,
  preset,
  settings = {},
  language = "english",
  availabilityContext = {},
  action,
  favoriteControl,
  getPriceQuote,
  merchantTag,
  onActivate,
  preparationTime,
  stockWarning,
}) => {
  const createInitialState = () => ({
    productId: product?.id,
    ...resolveInitialVariantState(product, availabilityContext),
  });
  const [variantState, setVariantState] = useState(createInitialState);
  const activeVariantState =
    variantState.productId === product?.id
      ? variantState
      : createInitialState();
  const {
    defaultChildId,
    priceSummary: selectedPriceSummary,
    product: selectedProduct,
    selection,
  } = activeVariantState;
  useEffect(() => {
    setVariantState(createInitialState());
  }, [product]);

  const displayedProduct = selectedProduct || product;
  const shortDescriptionKey = language === "arabic" ? "ar_short_description" : "short_description";
  const shortDescription = displayedProduct[shortDescriptionKey] ?? product[shortDescriptionKey];
  const quote = useMemo(
    () =>
      getPriceQuote
        ? getPriceQuote(
            selectedPriceSummary
              ? { ...displayedProduct, price_summary: selectedPriceSummary }
              : displayedProduct,
          )
        : createProductPriceQuote({
            product: selectedPriceSummary
              ? { ...displayedProduct, price_summary: selectedPriceSummary }
              : displayedProduct,
            settings,
          }),
    [displayedProduct, getPriceQuote, selectedPriceSummary, settings],
  );
  const availability = useMemo(
    () => resolveProductAvailability(displayedProduct, availabilityContext),
    [availabilityContext, displayedProduct],
  );
  const presentation = getContainerPresentation(surface, preset);
  const gridCard = ["grid", "bubbles"].includes(surface) && ["grid", "grid2", "bubbles"].includes(preset.id);
  const horizontal = !["search", "recommendations"].includes(surface) && preset.card.orientation === "horizontal";
  const jumboCard = horizontal && preset.id === "jumbo";
  // Jumbo's dashboard setting means physical left/right, including in Arabic.
  const imageAtEnd = jumboCard && language === "arabic"
    ? preset.card.imagePlacement === "start"
    : preset.card.imagePlacement === "end";
  const overlay = !["search", "recommendations"].includes(surface) && preset.card.orientation === "overlay";
  // If a variant borrows its parent's photo, keep that photo's fitting rule too.
  const imageProduct = displayedProduct.photo_medium || displayedProduct.photo_small || displayedProduct.photo
    ? displayedProduct
    : product;
  const image = imageProduct?.photo_medium || imageProduct?.photo_small || imageProduct?.photo || productPlaceholder;
  const themeColor = settings.dark || "#0099cc";
  const currentVariantState = {
    defaultChildId,
    priceSummary: selectedPriceSummary,
    selection,
  };
  const slotContext = { ...currentVariantState, availability };
  const actionContent =
    typeof action === "function"
      ? action(displayedProduct, currentVariantState)
      : action;
  const favoriteContent = resolveVisualSlot(
    favoriteControl,
    displayedProduct,
    slotContext,
  );
  const merchantTagContent = resolveVisualSlot(
    merchantTag,
    displayedProduct,
    slotContext,
  );
  const merchantTagPlacement =
    React.isValidElement(merchantTagContent) &&
    merchantTagContent.props.placement === "down"
      ? "down"
      : "top";
  const preparationTimeContent = resolveVisualSlot(
    preparationTime,
    displayedProduct,
    slotContext,
  );
  const stockWarningContent = resolveVisualSlot(
    stockWarning,
    displayedProduct,
    slotContext,
  );

  return (
    <div className={presentation.className} style={presentation.style}>
      <article
        aria-label={
          language === "arabic"
            ? displayedProduct.ar_name || displayedProduct.name
            : displayedProduct.name
        }
        onClick={(event) => {
          if (!eventStartedInNestedControl(event) && onActivate) {
            onActivate(displayedProduct, currentVariantState);
          }
        }}
        onKeyDown={(event) => {
          if (
            !eventStartedInNestedControl(event) &&
            (event.key === "Enter" || event.key === " ") &&
            onActivate
          ) {
            event.preventDefault();
            onActivate(displayedProduct, currentVariantState);
          }
        }}
        role="button"
        className={`sf-product-card${surface === "search" ? " sf-product-card--search" : ""}${gridCard ? " sf-product-card--grid" : ""}${horizontal ? " sf-product-card--horizontal" : ""}`}
        tabIndex={0}
        data-card-orientation={overlay ? "overlay" : horizontal ? "horizontal" : "vertical"}
        style={{
          background: overlay ? "#242424" : "white",
          border: "1px solid #ececec",
          borderRadius: 12,
          cursor: "pointer",
          direction: language === "arabic" ? "rtl" : "ltr",
          display: "flex",
          flexDirection: horizontal ? "row" : "column",
          height: "100%",
          minHeight: overlay ? 240 : undefined,
          overflow: "hidden",
          position: "relative",
          textAlign: language === "arabic" ? "right" : "left",
        }}
      >
        <div className="sf-product-card__promotion" style={horizontal && imageAtEnd ? { insetInlineStart: "auto", insetInlineEnd: 8 } : undefined}>
          <PromotionBadge language={language} quote={quote} settings={settings} />
        </div>
        <img
            alt=""
            data-product-placeholder={image === productPlaceholder ? "true" : undefined}
            loading="lazy"
            src={image}
            style={{
              alignSelf: horizontal ? "center" : "stretch",
              background: "#fafafa",
              flex: "0 0 auto",
              height: gridCard || horizontal ? "auto" : overlay ? "100%" : surface === "search" ? 220 : 150,
              aspectRatio: gridCard ? "var(--sf-product-grid-image-ratio, 1 / 1)" : horizontal ? "1" : undefined,
              borderRadius: horizontal ? 8 : undefined,
              marginInlineStart: horizontal && !imageAtEnd ? 10 : undefined,
              marginInlineEnd: horizontal && imageAtEnd ? 10 : undefined,
              marginBlock: horizontal ? 10 : undefined,
              inset: overlay ? 0 : undefined,
              objectFit: image === productPlaceholder ? "contain" : productImageFit(imageProduct, settings),
              order: !["search", "recommendations"].includes(surface) && imageAtEnd ? 2 : 0,
              position: overlay ? "absolute" : undefined,
              width: horizontal ? (jumboCard ? "40%" : "28%") : "100%",
            }}
          />
        <div
          className="sf-product-card__content"
          data-overlay-metadata={overlay ? "true" : undefined}
          style={{
            background: overlay
              ? "linear-gradient(180deg, rgba(0,0,0,0) 18%, rgba(0,0,0,0.88) 100%)"
              : undefined,
            color: overlay ? "white" : undefined,
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            gap: 8,
            justifyContent: overlay ? "flex-end" : undefined,
            minHeight: overlay ? 240 : undefined,
            minWidth: 0,
            padding: overlay ? "70px 12px 12px" : surface === "search" ? 16 : gridCard ? 12 : 10,
            position: overlay ? "relative" : undefined,
            textShadow: overlay ? "0 1px 2px rgba(0,0,0,0.8)" : undefined,
            width: overlay ? "100%" : undefined,
            zIndex: overlay ? 1 : undefined,
          }}
        >
          {merchantTagContent && merchantTagPlacement === "top" && (
            <div
              data-card-slot="merchant-tag"
              data-card-slot-placement="top"
            >
              {merchantTagContent}
            </div>
          )}
          <div
            style={{
              alignItems: "flex-start",
              display: "flex",
              gap: 6,
              minWidth: 0,
            }}
          >
            <div
              style={{
                flex: "1 1 auto",
                minWidth: 0,
                whiteSpace: "normal",
              }}
            >
              {stockWarningContent && (
                <span data-card-slot="stock-warning">
                  {stockWarningContent}
                </span>
              )}
              <strong className="sf-product-card__name" title={displayedProduct.name}>
                {productDisplayName(displayedProduct, language)}
              </strong>
            </div>
            {favoriteContent && (
              <div
                data-card-slot="favorite"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {favoriteContent}
              </div>
            )}
          </div>
          {(preparationTimeContent || (availability && availability.status !== "available")) && <div
            className="sf-product-card__status"
            style={{
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            <AvailabilityToken availability={availability} language={language} />
            {preparationTimeContent && (
              <span data-card-slot="preparation-time">
                {preparationTimeContent}
              </span>
            )}
          </div>}
          {!settings.hide_short_descriptions && shortDescription && <p className="sf-product-card__description">{shortDescription}</p>}
          {settings.show_nutrition_facts && <DietaryIcons product={displayedProduct} language={language} />}
          <PrimaryVariantChips
            availabilityContext={availabilityContext}
            color={themeColor}
            language={language}
            onChange={(dimension, choice) => {
              const resolvedSelection = getVariantSelection(choice.product);
              const nextSelection = Object.keys(resolvedSelection).length
                ? resolvedSelection
                : {
                    ...selection,
                    [dimension.key]: choice.value,
                  };
              setVariantState({
                defaultChildId:
                  choice.defaultChildId || choice.product?.id || null,
                priceSummary:
                  choice.priceSummary || choice.product?.price_summary || null,
                product: choice.product || null,
                productId: product?.id,
                selection: nextSelection,
              });
            }}
            onOverflow={() => {
              if (onActivate) {
                onActivate(displayedProduct, currentVariantState);
              }
            }}
            product={product}
            selection={selection}
          />
          {settings.show_prices !== false && (
            <div className="sf-product-card__price">
            <PriceBlock
              color={overlay ? "white" : themeColor}
              inverted={overlay}
              language={language}
              quote={quote}
              settings={settings}
              showPriceOnSelection
            />
            </div>
          )}
          {merchantTagContent && merchantTagPlacement === "down" && (
            <div
              data-card-slot="merchant-tag"
              data-card-slot-placement="down"
            >
              {merchantTagContent}
            </div>
          )}
          {actionContent && (
            <div
              className="sf-product-card__action"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              style={{ marginTop: surface === "search" ? 4 : "auto", width: "100%" }}
            >
              {actionContent}
            </div>
          )}
        </div>
      </article>
    </div>
  );
};

export default SharedProductCard;
